/**
 * Schedule a high-risk screening call on behalf of an existing client (Service Request Tracker).
 *
 * Reuses the intake screening availability UI/logic (AppointmentDateTimePicker +
 * getHighRiskAvailability) but books via the staff `book-screening-call` path, which creates only
 * the screening-call Appointment for the already-known patient. The admin places the call, so the
 * client's phone number is surfaced at the top.
 */

import { Alert, Button, Center, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { IconPhone } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppointmentDateTimePicker } from '../../../components/AppointmentDateTimePicker';
import { bookableDateKeysInWindow } from '../../public/intake/intakeDataService';
import { getIntakeBookingWindow } from '../../public/intake/bookingConstraints';
import { formatUsTimeZoneLabel } from '../../../lib/usTimeZoneLabel';
import { toFhirDateOnlyLocal } from '../../../lib/dateHelpers';
import {
  bookHighRiskScreeningCall,
  getHighRiskAvailability,
  isHighRiskBookingConflict,
  type HighRiskAvailabilitySlot,
} from '../../public/intake/highRiskIntakeScheduling';
import { DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE } from '../../../features/calendar/providerAvailabilitySchedule';

interface ScheduleCallModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | null;
  clientName?: string;
  /** Called after a call is successfully scheduled (or found already booked). */
  onScheduled?: () => void;
}

interface ResolvedSlot {
  start: string;
  end: string;
  practitionerId: string;
}

/** ISO wall-time "HH:mm:ss" used by the TimeGrid (slice from the offset-local ISO string). */
function isoToTimeKey(iso: string): string {
  return iso.slice(11, 19);
}

function readPhone(patient: Patient | undefined): string | undefined {
  return patient?.telecom?.find((t) => t.system === 'phone')?.value?.trim() || undefined;
}

export function ScheduleCallModal({
  open,
  onClose,
  patientId,
  clientName,
  onScheduled,
}: ScheduleCallModalProps): JSX.Element {
  const medplum = useMedplum();
  const { minDate, maxDate } = useMemo(() => getIntakeBookingWindow(), []);

  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [resolvedSlot, setResolvedSlot] = useState<ResolvedSlot | null>(null);

  const [slots, setSlots] = useState<HighRiskAvailabilitySlot[]>([]);
  const [loadedDateKey, setLoadedDateKey] = useState<string | null>(null);
  const [availableWeekdays, setAvailableWeekdays] = useState<string[]>([]);
  const [timeZone, setTimeZone] = useState(DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  /** True while the initial availability probe runs (drives which weekdays are selectable). */
  const [weekdaysLoading, setWeekdaysLoading] = useState(false);

  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);

  const loadSeqRef = useRef(0);

  // Reset all state each time the modal opens for a (possibly different) client, then probe the
  // screening team's availability so the date picker only enables bookable weekdays up front
  // (availableWeekdays is the team's recurring weekly set, independent of the probed date).
  useEffect(() => {
    if (!open) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setResolvedSlot(null);
    setSlots([]);
    setLoadedDateKey(null);
    setAvailableWeekdays([]);
    setSlotsLoading(false);
    setAvailabilityError(null);
    setBooking(false);
    setBookError(null);
    setPatient(undefined);
    setWeekdaysLoading(true);
    if (patientId) {
      medplum
        .readResource('Patient', patientId)
        .then(setPatient)
        .catch(() => setPatient(undefined));
    }

    let cancelled = false;
    const probeDateKey = toFhirDateOnlyLocal(minDate);
    getHighRiskAvailability(medplum, { date: probeDateKey })
      .then((r) => {
        if (cancelled) return;
        setAvailableWeekdays(r.availableWeekdays);
        setTimeZone(r.timeZone);
      })
      .catch(() => {
        /* leave availableDates undefined → all days selectable as a safe fallback */
      })
      .finally(() => {
        if (!cancelled) setWeekdaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, patientId, medplum, minDate]);

  const availableDates = useMemo(() => {
    if (availableWeekdays.length === 0) return undefined;
    return bookableDateKeysInWindow(minDate, maxDate, availableWeekdays);
  }, [availableWeekdays, minDate, maxDate]);

  const loadSlotsForDate = useCallback(
    async (dateKey: string): Promise<void> => {
      const loadSeq = ++loadSeqRef.current;
      setSlotsLoading(true);
      setAvailabilityError(null);
      setSlots([]);
      try {
        const result = await getHighRiskAvailability(medplum, { date: dateKey });
        if (loadSeq !== loadSeqRef.current) return;
        setSlots(result.slots);
        setLoadedDateKey(dateKey);
        setAvailableWeekdays(result.availableWeekdays);
        setTimeZone(result.timeZone);
      } catch {
        if (loadSeq !== loadSeqRef.current) return;
        setSlots([]);
        setLoadedDateKey(null);
        setAvailabilityError('Could not load availability for this date.');
      } finally {
        if (loadSeq === loadSeqRef.current) setSlotsLoading(false);
      }
    },
    [medplum],
  );

  const liveTimeSlotsForDate = useCallback(
    (dateKey: string): string[] => {
      if (dateKey !== loadedDateKey) return [];
      return slots.map((s) => isoToTimeKey(s.start));
    },
    [slots, loadedDateKey],
  );

  const handleDateChange = useCallback(
    (dateKey: string) => {
      if (dateKey === loadedDateKey) return;
      setSelectedDate(dateKey);
      setSelectedTime(null);
      setResolvedSlot(null);
      setBookError(null);
      void loadSlotsForDate(dateKey);
    },
    [loadSlotsForDate, loadedDateKey],
  );

  const handleTimeChange = useCallback(
    (time: string | null) => {
      setSelectedTime(time);
      setBookError(null);
      if (!time) {
        setResolvedSlot(null);
        return;
      }
      const normalized = time.length === 5 ? `${time}:00` : time;
      const slot = slots.find((s) => isoToTimeKey(s.start) === normalized);
      setResolvedSlot(
        slot ? { start: slot.start, end: slot.end, practitionerId: slot.practitionerId } : null,
      );
    },
    [slots],
  );

  const handleConfirm = useCallback(async () => {
    if (!patientId || !resolvedSlot) return;
    setBooking(true);
    setBookError(null);
    try {
      await bookHighRiskScreeningCall(medplum, {
        patientId,
        start: resolvedSlot.start,
        end: resolvedSlot.end,
        practitionerId: resolvedSlot.practitionerId,
      });
      onScheduled?.();
      onClose();
    } catch (err) {
      if (isHighRiskBookingConflict(err) && selectedDate) {
        setBookError('That time was just booked. Please pick another time.');
        setSelectedTime(null);
        setResolvedSlot(null);
        void loadSlotsForDate(selectedDate);
      } else {
        setBookError('Could not schedule the call. Please try again.');
      }
    } finally {
      setBooking(false);
    }
  }, [patientId, resolvedSlot, medplum, onScheduled, onClose, selectedDate, loadSlotsForDate]);

  const phone = readPhone(patient);

  return (
    <Modal opened={open} onClose={onClose} title="Schedule screening call" size="lg" centered>
      <Stack gap="md">
        <Alert variant="light" color="blue" icon={<IconPhone size={18} />}>
          <Text size="sm" fw={500}>
            {clientName ?? 'Client'}
          </Text>
          <Text size="sm" c={phone ? undefined : 'dimmed'} mt={2}>
            {phone ? `Call: ${phone}` : 'No phone number on file'}
          </Text>
        </Alert>

        <Text size="sm" c="dimmed">
          Choose a day and time for the 15-minute high-risk screening call.
        </Text>

        {availabilityError ? (
          <Text size="sm" c="red">
            {availabilityError}
          </Text>
        ) : null}
        {!slotsLoading && loadedDateKey && slots.length === 0 ? (
          <Text size="sm" c="dimmed">
            No open times on this day. Try another date.
          </Text>
        ) : null}

        {weekdaysLoading ? (
          <Center py="xl">
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading availability…
              </Text>
            </Group>
          </Center>
        ) : (
          <AppointmentDateTimePicker
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateChange={handleDateChange}
            onTimeChange={handleTimeChange}
            minDate={minDate}
            maxDate={maxDate}
            liveTimeSlots={liveTimeSlotsForDate}
            availableDates={availableDates}
            timeZoneLabel={formatUsTimeZoneLabel(timeZone)}
            timesLoading={slotsLoading}
          />
        )}

        {bookError ? (
          <Text size="sm" c="red">
            {bookError}
          </Text>
        ) : null}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={booking}>
            Cancel
          </Button>
          <Button color="gold" onClick={handleConfirm} disabled={!resolvedSlot} loading={booking}>
            Schedule call
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
